import React, { useContext, useEffect, useRef, useState } from "react";
import { MantineReactTable, MRT_Icons, MRT_RowSelectionState, MRT_ShowHideColumnsButton, useMantineReactTable } from "mantine-react-table"
import { ActionIcon, CheckIcon, ColorSwatch, Group, Menu, rem, Stack, Text, Tooltip } from "@mantine/core"
import { StackedBarCell } from "../../components/Rankings/StackedBarCell";
import { css } from '@emotion/css';
import UserSelectionContext from "../../providers/UserSelectionProvider";
import DataContext from "../../providers/DataProvider";
import { getHardcodedColor, getHardcodedLabels } from "../../utils";
import { dataStreamTypes, scoreTypes } from "../../types";
import { FaSearch } from "react-icons/fa";
import VerticalBarChartHeader from "./WeightsBarChart";
import RankLabel from "./RankLabel";
import { BsCheckSquare, BsSquare } from "react-icons/bs";
import { useDebounce } from "use-debounce";
import { CiFilter } from "react-icons/ci";

const customIcons: Partial<MRT_Icons> = {
    IconFilter: (props: any) => (
        <FaSearch {...props} />
    ),
    // IconDotsVertical: (props) => <CiFilter {...props} />,
}

const RankingsTable = () => {
    const { rankings, weights, loadingRankings, scoresDomain, selectedRun } = useContext(DataContext)
    const [filteredRankings, setFilteredRankings] = useState<any>([])
    const { selectedIndications, selectedWeights, SetSelectedIndications, streamsEnabled, setStreamEnabled } = useContext(UserSelectionContext)
    const [hoveredRowId, setHoveredRowId] = useState(null);
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
    const [showSelected, setShowSelected] = useState<boolean>(false);
    const [debouncedSelection] = useDebounce(rowSelection, 200);
    const isFirstLoad = useRef(true);

    useEffect(() => {
        setFilteredRankings(showSelected ? rankings.filter(i => selectedIndications.has(i.name)) : rankings)
    }, [showSelected, selectedIndications, rankings]);

    useEffect(() => {
        const oldIndications = new Set(Object.keys(rowSelection));

        const isSameSet = (a, b) =>
            a.size === b.size && [...a].every((item) => b.has(item));

        if (isSameSet(oldIndications, selectedIndications))
            return

        setRowSelection(Object.fromEntries(Array.from(selectedIndications).map(i => [i, true])))

    }, [selectedIndications]);

    useEffect(() => {
        if (isFirstLoad.current) {
            isFirstLoad.current = false;
            return;
        }

        const newIndications = new Set(Object.keys(rowSelection));

        const isSameSet = (a, b) =>
            a.size === b.size && [...a].every((item) => b.has(item));

        if (!isSameSet(newIndications, selectedIndications))
            SetSelectedIndications(newIndications)

    }, [debouncedSelection]);

    const [sorting, setSorting] = useState([]);

    useEffect(() => {
        setSorting([
            {
                id: scoreTypes.mscore,
                desc: true
            }
        ]);
    }, [selectedWeights]);

    const columns = [
        {
            accessorKey: "rank",
            header: "Rank",
            size: 5,
            Cell: ({ cell }) => <RankLabel data={[
                { ...cell.getValue()[selectedWeights], key: selectedWeights },
                ...Object.entries(cell.getValue())
                    .filter(([k]) => k !== selectedWeights)
                    .map(([k, v]) => ({ key: k, ...v }))
            ]} />,
            enableSorting: false,
            enableColumnFilter: false,
            enableColumnActions: false,
            enableHiding: false,
        },
        {
            id: "empty0",
            columnDefType: 'display',
            header: "",
            size: 5,
            minSize: 1
        },
        {
            accessorKey: "name",
            header: "Indication",
            Cell: ({ cell, renderedCellValue }) => {
                return (
                    <div>
                        <Tooltip label={cell.getValue()} position="top-start" >
                            <Text size="xs" truncate="end">{renderedCellValue}</Text>
                        </Tooltip>
                    </div>
                )
            },
            size: 100,
            enableSorting: false,
            enableColumnFilter: true,
            enableColumnActions: true,
            enableHiding: false,
            headerStyle: { borderBottom: '1px solid #ccc' },
            mantineFilterTextInputProps: { placeholder: 'Search for indications' }
        },
        {
            id: "empty1",
            columnDefType: 'display',
            header: "",
            size: 5,
            minSize: 1
        },
        {
            accessorKey: scoreTypes.mscore,
            header: getHardcodedLabels(scoreTypes.mscore),
            Cell: ({ cell }) => <StackedBarCell data={[
                cell.getValue()[selectedWeights],
                ...Object.entries(cell.getValue()).filter(([k,]) => k !== selectedWeights).map(([k, v]) => v)
            ]} showValues={true} domain={scoresDomain} />,
            sortingFn: (rowA, rowB) => {
                const mscoreA = rowA.original.mscore[selectedWeights][scoreTypes.mscore];
                const mscoreB = rowB.original.mscore[selectedWeights][scoreTypes.mscore];
                if (mscoreA < mscoreB) return -1;
                if (mscoreA > mscoreB) return 1;
                return 0;
            },
            enableFilters: false,
            mantineTableBodyCellProps: {
                style: {
                    paddingTop: 0,
                    paddingBottom: 0
                }
            },
            enableSorting: true,
            enableColumnFilter: false,
            enableColumnActions: false,
            headerStyle: { borderBottom: '1px solid #ccc' },
        },
        {
            id: "empty2",
            columnDefType: 'display',
            header: "",
            size: 5,
            minSize: 1
        },
        {
            accessorKey: scoreTypes.detailedScore,
            header: getHardcodedLabels(scoreTypes.detailedScore),
            Header: () => (
                <Stack gap={0}>
                    <Text size="sm" fw={700}>{getHardcodedLabels(scoreTypes.detailedScore)}</Text>
                    {!loadingRankings ?
                        <VerticalBarChartHeader data={weights[selectedRun.run_id][selectedWeights]} run_id={selectedRun.run_id} /> :
                        <></>}
                </Stack>
            ),
            Cell: ({ cell }) => <StackedBarCell data={[
                cell.getValue()[selectedWeights],
                ...Object.entries(cell.getValue()).filter(([k,]) => k !== selectedWeights).map(([k, v]) => v)
            ]} showValues={cell.row.id === hoveredRowId} domain={scoresDomain} />,
            enableFilters: false,
            mantineTableBodyCellProps: {
                style: {
                    paddingTop: 0,
                    paddingBottom: 0
                }
            },
            enableSorting: false,
            enableColumnFilter: false,
            enableColumnActions: false,
            headerStyle: { borderBottom: '1px solid #ccc' },
        },
    ]

    const table = useMantineReactTable({
        columns, data: filteredRankings,
        enableRowSelection: true,
        enableSelectAll: false,
        enableColumnResizing: false,
        enableDensityToggle: false,
        icons: customIcons,
        state: { isLoading: loadingRankings, sorting: sorting, rowSelection },
        mantineToolbarAlertBannerProps: {
            display: 'none'
        },
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        getRowId: (originalRow) => originalRow.name,
        mantineTableHeadCellProps: ({ column }) => {
            if (column.id === 'mrt-row-select') {
                return {
                    style: {
                        width: '5%',
                        color: 'transparent',
                        padding: 1,
                        paddingBottom: 10,
                        verticalAlign: 'bottom'
                    },
                };
            }
            return {
                style: {
                    borderBottom:
                        column.id === 'name'
                            ? '1px solid #000'
                            : column.id === 'mscore' || column.id === 'rank'
                                ? '1px solid #000'
                                : column.id === 'detailed_score'
                                    ? '1px solid #000'
                                    : '0px',
                    padding: 0,
                    paddingBottom: 10,
                    verticalAlign: 'bottom'
                },
            };
        },
        mantineTableProps: {
            style: {
                borderCollapse: 'separate',
                borderSpacing: '0',
                tableLayout: "fixed",
            }
        },
        enableGlobalFilter: false,
        initialState: {
            showColumnFilters: true,
            density: 'xs',
            pagination: {
                pageIndex: 0,
                pageSize: 30
            },
            sorting: [
                {
                    id: scoreTypes.mscore,
                    desc: true
                }
            ]
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: row.getToggleSelectedHandler(),
            style: { borderBottom: "block", cursor: 'pointer' },
            onMouseEnter: () => { setHoveredRowId(row.id) },
            onMouseLeave: () => { setHoveredRowId(null) },
        }),
        enableStickyHeader: true,
        mantineSelectCheckboxProps: {
            size: 'xs'
        },
        mantineBottomToolbarProps: {
            style: {
                marginBottom: "1px",
                padding: "5px"
            }
        },
        mantinePaperProps: {
            withBorder: false,
            shadow: 'initial',
            style: {
                flex: 1,
                width: '100%',
                maxWidth: 1500,
                height: '100%',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column'
            },
            // Make the table cells smaller
            className: css`
              .mantine-Table-td {
                padding: 0px !important;
                padding-top: 1px !important;
                padding-bottom: 1px !important;
              }
            `
        },
        mantineTableContainerProps: {
            style: {
                overflow: 'hidden',
                scrollbarWidth: 'thin',
            },
            onMouseEnter: (e) => {
                e.currentTarget.style.overflow = 'auto';
            },
            onMouseLeave: (e) => {
                e.currentTarget.style.overflow = 'hidden';
            },
        },
        renderToolbarInternalActions: ({ table }) => (
            <>
                <MRT_ShowHideColumnsButton table={table} />
            </>
        ),
        renderTopToolbarCustomActions: ({ table }) => (
            <Group justify="flex-start" style={{ padding: 0 }}>
                {Object.values(dataStreamTypes).map((t) =>
                    <Group>
                        <ColorSwatch
                            component="button"
                            color={getHardcodedColor(t)}
                            onClick={() => setStreamEnabled({ ...streamsEnabled, [t]: !streamsEnabled[t] })}
                            size={20}
                            style={{ color: '#fff', cursor: 'pointer' }}
                        >
                            {streamsEnabled[t] && <CheckIcon style={{ width: rem(10), height: rem(10) }} />}
                        </ColorSwatch>
                        <Text size="sm">{getHardcodedLabels(t)}</Text>
                    </Group>
                )}
            </Group>
        ),
        renderColumnActionsMenuItems: ({ column }) => {
            if (column.id === 'name') return (
                <>
                    <Menu.Item
                        leftSection={showSelected ? <BsCheckSquare /> : <BsSquare />}
                        onClick={() => setShowSelected((prev) => !prev)}
                    >
                        Only selected
                    </Menu.Item>
                </>
            )
        }
    })

    return (
        <MantineReactTable table={table} />
    );
}

export default RankingsTable;
